import { point as turfPoint } from '@turf/turf';
import chroma from 'chroma-js';
import { nanoid } from 'nanoid';
import { createCircleFromPoint } from '~/common/geoUtils';
import type { CircleObject as TCircleObject } from '~/common/types';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateArea } from '~/helpers/unitHelpers';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { FONT_SIZE, getCircleDestCoords, LABEL_OFFSET, LABEL_PADDING } from './common';

export default class CircleObject extends CanvasObject {
  circle: TCircleObject;
  center: VertexObject;

  constructor(id: string, surface: SurfaceBase, circle: TCircleObject) {
    super(id, surface);
    this.circle = circle;
    const [lng, lat] = this.circle.geometry.coordinates;

    this.center = this.createVertex({ lng, lat });

    this.addChild(this.center);
  }

  get outerPoint() {
    const { coordinates } = getCircleDestCoords({
      lngLat: this.center.lngLat,
      radius: this.circle.properties.radius,
    }).geometry;

    return this.surface.toScreenCoords({
      lng: coordinates[0],
      lat: coordinates[1],
    });
  }

  get radiusInPixels() {
    return Math.abs(this.outerPoint.x - this.center.point.x);
  }

  createVertex(lngLat: LngLat) {
    const v = new VertexObject(nanoid(), this.surface, {
      lngLat,
      point: this.surface.toScreenCoords(lngLat),
    });

    return v;
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    const { x, y } = this.center.point;

    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, this.radiusInPixels, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { properties } = this.circle;
    const { x, y } = this.center.point;
    const r = this.radiusInPixels;

    const { strokeStyle: lineDash, strokeOpacity, strokeWidth, color, fillOpacity } = properties;

    ctx.globalAlpha = 1;
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = color;
    ctx.fillStyle = chroma(color).alpha(fillOpacity).hex();
    lineDash === 'dashed' && ctx.setLineDash([10, 4]);

    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = strokeOpacity;
    ctx.stroke();

    ctx.font = `bold ${FONT_SIZE}px Inter`;

    if (properties.showArea) {
      // render labels
      const { lng, lat } = this.center.lngLat;

      const circlePoly = createCircleFromPoint({
        center: turfPoint([lng, lat]).geometry,
        radius: this.circle.properties.radius,
      });

      const a = calculateArea({
        unitSystem: this.getStore().currentUnitSystem,
        geometry: circlePoly,
      });

      const areaText = `${a.value.toLocaleString()} ${a.unit}`;
      const areaTextWidth = ctx.measureText(areaText).width;
      const circleWidth = r * 2;

      const exceedsWidth = areaTextWidth + LABEL_PADDING > circleWidth;

      if (!exceedsWidth) {
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.translate(x, y);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(
          -areaTextWidth / 2 - LABEL_PADDING / 2,
          -FONT_SIZE - LABEL_OFFSET,
          areaTextWidth + LABEL_PADDING,
          FONT_SIZE + LABEL_PADDING,
          [5],
        );
        ctx.fill();

        ctx.fillStyle = BRIGHT_COLORS.includes(color) ? '#000000' : '#FFFFFF';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillText(areaText, 0, -FONT_SIZE / 2 - LABEL_OFFSET - LABEL_PADDING / 4);
        ctx.restore();
      }
    }

    if (properties.showRadius) {
      const o = this.circle as TCircleObject;
      // show radius too
      const radiusInMiles = (o.properties.radius / (o.properties.useMilesToDisplayRadius ? 5_280 : 1)).toLocaleString();
      const radiusText = `R= ${radiusInMiles} ${o.properties.useMilesToDisplayRadius ? 'mi' : 'ft'}`;
      const radiusTextWidth = ctx.measureText(radiusText).width;

      const radTextExceeds = radiusTextWidth + LABEL_PADDING > r;
      if (!radTextExceeds) {
        const midPoint: Point = {
          x: x + (this.outerPoint.x - x) / 2,
          y: y + (this.outerPoint.y - y) / 2,
        };

        // draw rad line
        ctx.save();
        ctx.lineWidth = 2;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(this.outerPoint.x, this.outerPoint.y);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.translate(midPoint.x, midPoint.y);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(
          -radiusTextWidth / 2 - LABEL_PADDING / 2,
          LABEL_OFFSET / 2,
          radiusTextWidth + LABEL_PADDING,
          FONT_SIZE + LABEL_PADDING,
          [5],
        );
        ctx.fill();

        ctx.fillStyle = BRIGHT_COLORS.includes(color) ? '#000000' : '#FFFFFF';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillText(radiusText, 0, FONT_SIZE / 2 + LABEL_OFFSET / 4 + LABEL_PADDING / 4);
        ctx.restore();
      }
    }
  }
}
